import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';

import * as GoogleDriveActions from './file-explorer.actions';
import { DriveControllerService } from '@rappider/rappider-sdk';
import { BrowserStorageManagementService, NotificationService } from '@rappider/services';
import { Action, Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { FullPaths } from 'libs/shared/src/lib/definitions/page-definition';

@Injectable()
export class FileExplorerEffects {
  constructor(
    private actions$: Actions,
    private driveControllerService: DriveControllerService,
    private notificationService: NotificationService,
    private browserStorageManagementService: BrowserStorageManagementService,
    private router: Router,
    private store: Store<any>
  ) { }

  fileExplorerConnect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        GoogleDriveActions.ConnectFileExplorer
      ),
      mergeMap((action) => {
        const params = {
          provider: action.payload.provider,
          authorizationToken: this.browserStorageManagementService.getLocalStorageItem('Authorization'),
          accessKey: action.payload.accessKey,
          secretAccessKey: action.payload.secretAccessKey,
          region: action.payload.region,
          bucketName: action.payload.bucketName
        };
        if (!((action as any).payload as any)?.accessKey) {
          delete params.accessKey
        }
        if (!((action as any).payload as any)?.secretAccessKey) {
          delete params.secretAccessKey
        }
        if (!((action as any).payload as any)?.region) {
          delete params.region
        }
        if (!((action as any).payload as any)?.bucketName) {
          delete params.bucketName
        }
        return this.driveControllerService.connect(params).pipe(
          mergeMap((connect) => {
            return [
              GoogleDriveActions.ConnectFileExplorerSuccessful(),
              GoogleDriveActions.GetConnectedAccount()
            ];
          }), catchError(error => {
            return [
              GoogleDriveActions.ConnectFileExplorerFailure({ payload: { error, key: 'ConnectFileExplorerFailure', timestamp: Date.now() } }),
              GoogleDriveActions.GetConnectedAccount()
            ];
          }
          ));
      }
      )
    )
  );

  fileExplorerFileListWithSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        GoogleDriveActions.GetFileExplorerFileList
      ),
      withLatestFrom(
        this.store.select(state => state.fileExplorer.activeProvider),
      ),
      mergeMap(([action, activeProvider]) => {
        let params = {
          provider: activeProvider,
          fileName: ((action as any).payload as any)?.searchText,
          parentId: ((action as any).payload as any)?.folderId
        };
        if (!((action as any).payload as any)?.searchText) {
          delete params.fileName
        }
        if (!((action as any).payload as any)?.folderId) {
          delete params.parentId
        }
        return this.driveControllerService.listFiles(params).pipe(
          mergeMap((files) => {
            const actions: Action[] = [];
            if (((action as any).payload as any)?.folderName) {
              actions.push(GoogleDriveActions.OrderFileExplorer({ payload: { folder: { id: (action.payload as any)?.folderId, name: (action.payload as any)?.folderName } } }));
            }
            actions.push(GoogleDriveActions.GetFileExplorerFileListSuccessful({ payload: { files } }));
            return actions;
          }), catchError(error => {
            return [
              GoogleDriveActions.GetFileExplorerFileListFailure({ payload: { error, key: 'GetFileExplorerFileListFailure', timestamp: Date.now() } })
            ];
          }
          ));
      }
      )
    )
  );

  ingestMultipleFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        GoogleDriveActions.IngestMultipleFilesExplorer
      ),
      withLatestFrom(
        this.store.select(state => state.fileExplorer.activeProvider),
      ),
      mergeMap(([action, activeProvider]) => {
        const params = {
          provider: activeProvider,
          body: { fileIds: action.payload.fileIds, isApproved: action.payload.isApproved, fileMetadata: action.payload.fileMetadata, categoryId:action.payload.categoryId }
        };
        return this.driveControllerService.ingestMultipleDriveFiles(params).pipe(
          map((files) => {
            this.notificationService.createNotification(
              'success',
              'Success',
              'File Save Successful'
            );
            this.router.navigate(['ai/datasources']);
            return GoogleDriveActions.IngestMultipleFilesExplorerSuccessful();
          }), catchError(error => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              error || 'Ingest failed'
            );
            return [
              GoogleDriveActions.IngestMultipleFilesExplorerFailure({ payload: { error, key: 'IngestMultipleFilesExplorerFailure', timestamp: Date.now() } })
            ];
          }
          ));
      }
      )
    )
  );

  getConnectedAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        GoogleDriveActions.GetConnectedAccount
      ),
      withLatestFrom(
        this.store.select(state => state.fileExplorer.activeProvider),
      ),
      mergeMap(([action, activeProvider]) => {
        const params = {
          provider: activeProvider
        };
        return this.driveControllerService.getConnectedAccountByProvider(params).pipe(
          map((connectedAccount) => {
            return GoogleDriveActions.GetConnectedAccountSuccessful({ payload: { connectedAccount } });
          }), catchError(error => {
            return [
              GoogleDriveActions.GetConnectedAccountFailure({ payload: { error: error?.error?.error?.message || 'No connection found', key: 'GetConnectedAccountFailure', timestamp: Date.now() } })
            ];
          }
          ));
      }
      )
    )
  );

  getConnectedAccountList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        GoogleDriveActions.GetConnectedAccountList
      ),
      mergeMap((action) => {
        const params = {
          provider: action.payload.provider
        };
        return this.driveControllerService.getConnectedAccountByProvider(params).pipe(
          map((connectedAccount) => {
            return GoogleDriveActions.GetConnectedAccountListSuccessful({ payload: { connectedAccount } });
          }), catchError(error => {
            return [
              GoogleDriveActions.GetConnectedAccountListFailure({ payload: { error: error?.error?.error?.message || 'No connection found', key: 'GetConnectedAccountFailure', timestamp: Date.now() } })
            ];
          }
          ));
      }
      )
    )
  );

  updateConnectedAccountById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        GoogleDriveActions.UpdateConnectedAccountById
      ),
      mergeMap((action) => {
        const params = {
          id: action.payload.id,
          body: {
            isFileAccessShared: action.payload.isFileAccessShared
          }
        };
        return this.driveControllerService.updateById(params).pipe(
          map((connectedAccount) => {
            this.notificationService.createNotification(
              'success',
              'Success',
              action.payload.isFileAccessShared ? 'Allowed for others to access my files' : 'Not Allowed for others to access my files'
            );
            return GoogleDriveActions.UpdateConnectedAccountByIdSuccessful({ payload: { isFileAccessShared: action.payload.isFileAccessShared } });
          }), catchError(error => {
            this.notificationService.createNotification(
              'error',
              'Error',
              'An error occurred'
            );
            return [
              GoogleDriveActions.UpdateConnectedAccountByIdFailure({ payload: { error, key: 'UpdateConnectedAccountByIdFailure', timestamp: Date.now() } })
            ];
          }
          ));
      }
      )
    )
  );

  deleteConnectedAccountByProvider$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        GoogleDriveActions.DeleteConnectedAccountByProvider
      ),
      mergeMap((action) => {
        const params = {
          provider: action.payload.provider
        };
        return this.driveControllerService.deleteConnectedAccountByProvider(params).pipe(
          map(() => {
            return GoogleDriveActions.DeleteConnectedAccountByProviderSuccessful();
          }), catchError(error => {
            return [
              GoogleDriveActions.DeleteConnectedAccountByProviderFailure({ payload: { error, key: 'DeleteConnectedAccountByProviderFailure', timestamp: Date.now() } })
            ];
          }
          ));
      }
      )
    )
  );

  checkFileViewPermission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        GoogleDriveActions.CheckFileViewPermisson
      ),
      withLatestFrom(
        this.store.select(state => state.fileExplorer.activeProvider),
      ),
      mergeMap(([action, provider]) => {
        return this.driveControllerService.listFiles({ provider }).pipe(
          map(() => {
            this.router.navigate([FullPaths.AIDataSourcesFileExplorerList]);
            return GoogleDriveActions.CheckFileViewPermissonSuccessful();
          }), catchError(error => {
            this.notificationService.createNotification(
              'error',
              error.statusText,
              error.error.error.message
            );
            return [
              GoogleDriveActions.CheckFileViewPermissonFailure({ payload: { error, key: 'CheckFileViewPermissontFailure', timestamp: Date.now() } })
            ];
          }
          ));
      }
      )
    )
  );

}
